import React, { createContext, useContext, useEffect, useState } from 'react'
import { loadGoogleMaps } from '~utils/google-maps-loader'

interface GoogleMapsContextType {
    isLoaded: boolean
    error: Error | null
    google: typeof google | null
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
    isLoaded: false,
    error: null,
    google: null,
})

interface GoogleMapsProviderProps {
    children: React.ReactNode
}

/**
 * GoogleMapsProvider loads the Google Maps API once at the application level
 * and provides it to all child components through context.
 *
 * This prevents the "gJ" error by ensuring the API is only loaded once.
 */
export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({
    children,
}) => {
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState<Error | null>(null)
    const [google, setGoogle] = useState<typeof window.google | null>(null)

    useEffect(() => {
        // Only load on client side to prevent SSR issues
        if (typeof window === 'undefined') {
            return
        }

        let isMounted = true

        const loadMaps = async () => {
            try {
                const googleMaps = await loadGoogleMaps()
                if (isMounted) {
                    setGoogle(googleMaps)
                    setIsLoaded(true)
                }
            } catch (err) {
                if (isMounted) {
                    setError(
                        err instanceof Error
                            ? err
                            : new Error('Failed to load Google Maps'),
                    )
                }
            }
        }

        // Add a small delay to ensure DOM is ready
        const timeoutId = setTimeout(loadMaps, 100)

        return () => {
            isMounted = false
            clearTimeout(timeoutId)
        }
    }, [])

    const contextValue: GoogleMapsContextType = {
        isLoaded,
        error,
        google,
    }

    return (
        <GoogleMapsContext.Provider value={contextValue}>
            {children}
        </GoogleMapsContext.Provider>
    )
}

/**
 * Hook to access Google Maps API from components
 */
export const useGoogleMaps = (): GoogleMapsContextType => {
    const context = useContext(GoogleMapsContext)

    if (!context) {
        throw new Error(
            'useGoogleMaps must be used within a GoogleMapsProvider',
        )
    }

    return context
}
